import React from 'react';
import { createPortal } from 'react-dom';
import { buttonVariants, tabbablesIn } from '@charitystack/shared';
import { cn } from '../lib/utils';
import { escapeBelongsToOpenPopup } from '../Util/overlayEscape';

/** The attribute that marks the card or pane a PaneSheet covers. */
export const PANE_SHEET_SCOPE_ATTR = 'data-pane-sheet-scope';
const SCOPE_SELECTOR = `[${PANE_SHEET_SCOPE_ATTR}]`;
/**
 * Marks the control that takes focus when a sheet closes and the control that opened it is gone
 * (saving a schedule replaces its link with the summary, whose Edit carries this).
 */
export const PANE_SHEET_RETURN_ATTR = 'data-pane-sheet-return';
export const PANE_SHEET_OPEN_ATTR = 'data-pane-sheet-open';

/**
 * Open / close state for a PaneSheet and the control that opens it. `inline` renders the sheet
 * into the nearest [data-pane-sheet-scope] ancestor of that control (the card it covers);
 * otherwise it renders in place. The control is `triggerRef` when the caller renders it, or the
 * element `open` was called from (a click event's currentTarget, else the focused element), so a
 * sheet can be opened by a control another component owns. Closing returns focus to it, or, when
 * it has left the page, to the scope's [data-pane-sheet-return] control.
 */
export function usePaneSheet({ inline = false } = {}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [container, setContainer] = React.useState(null);
  const triggerRef = React.useRef(null);
  const originRef = React.useRef(null);
  const scopeRef = React.useRef(null);
  const wasOpenRef = React.useRef(false);

  React.useEffect(() => {
    if (!isOpen && wasOpenRef.current) {
      const origin = originRef.current;
      const target = origin?.isConnected
        ? origin
        : (scopeRef.current || document).querySelector?.(`[${PANE_SHEET_RETURN_ATTR}]`);
      target?.focus?.({ preventScroll: true });
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  const open = (event) => {
    const origin =
      triggerRef.current ||
      (event?.currentTarget instanceof Element ? event.currentTarget : null) ||
      (typeof document !== 'undefined' ? document.activeElement : null);
    originRef.current = origin;
    scopeRef.current = origin?.closest?.(SCOPE_SELECTOR) || null;
    if (inline) setContainer(scopeRef.current);
    setIsOpen(true);
  };
  const close = () => setIsOpen(false);

  return { isOpen, open, close, triggerRef, container: inline ? container : null };
}

/**
 * The sheet that slides up over a card or pane (CON-520, Paper 7K5-0): the scrim over the scope,
 * a sheet `--cs-pane-sheet-height` of it tall with the card-radius top and the sheet shadow, the 52 title
 * strip with a transparent 36 close, a scroll body with no scrollbar, and an optional pinned
 * `footer` (an action row such as Save). Modal while mounted:
 * Tab wraps inside it, everything else in the scope is inert, and one Escape inside it closes
 * only the sheet: it is taken on the window in the CAPTURE phase, before anything else hears it
 * (the vaul drawer or overlay the card sits in listens on the document in capture, Radix's
 * DismissableLayer), and stopped there. An Escape that belongs to an open popup inside the sheet
 * (`escapeBelongsToOpenPopup`: an expanded combobox / popup trigger, or a control owning a
 * rendered listbox / menu / dialog) is left to that popup. The pickers' own popovers render to
 * the body, so their Escape never reaches here. Mount it only while open.
 * FAQPaneDrawer, the detail pages' Learn more and the schedule sheet open this.
 *
 * `height`: 'pane' (default) is `--cs-pane-sheet-height` of the scope; 'content' fits its
 * content, capped at that height (the schedule sheet, whose fields are short).
 */
export default function PaneSheet({
  title,
  closeLabel,
  onClose,
  container = null,
  footer = null,
  height = 'pane',
  children,
}) {
  const dialogRef = React.useRef(null);
  const closeRef = React.useRef(null);
  const titleId = React.useId();

  React.useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;
    const doc = dialog.ownerDocument;
    const win = doc.defaultView;
    const inSheet = (event) => event.target instanceof Node && dialog.contains(event.target);
    // Window, capture: runs before the drawer's / overlay's capture listeners on the document,
    // so the sheet's Escape never also dismisses the drawer around it (QA round 2, F1).
    const onEscapeCapture = (event) => {
      if (event.key !== 'Escape' || !inSheet(event)) return;
      if (escapeBelongsToOpenPopup(event)) return;
      event.stopPropagation();
      event.preventDefault();
      onClose();
    };
    const onKeyDown = (event) => {
      if (event.key !== 'Tab' || !inSheet(event)) return;
      const tabbables = tabbablesIn(dialog);
      if (tabbables.length === 0) {
        event.preventDefault();
        return;
      }
      const first = tabbables[0];
      const last = tabbables[tabbables.length - 1];
      const active = dialog.ownerDocument.activeElement;
      if (event.shiftKey && (active === first || !dialog.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    win?.addEventListener('keydown', onEscapeCapture, true);
    doc.addEventListener('keydown', onKeyDown);
    return () => {
      win?.removeEventListener('keydown', onEscapeCapture, true);
      doc.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  // Everything the sheet covers goes inert, so neither a pointer, Tab nor a screen reader's
  // virtual cursor reaches it: every element on the path from the scope down to the dialog that
  // is neither the dialog nor the scrim. Prior attribute values are put back on close.
  React.useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;
    const scope = dialog.parentElement?.closest(SCOPE_SELECTOR) || dialog.parentElement;
    if (!scope) return undefined;
    const covered = [];
    for (let node = dialog; node && node !== scope; node = node.parentElement) {
      const parent = node.parentElement;
      if (!parent) break;
      for (const sibling of parent.children) {
        if (sibling === node || sibling.hasAttribute('data-faq-drawer-scrim')) continue;
        covered.push(sibling);
      }
    }
    const saved = covered.map((element) => ({
      element,
      inert: element.getAttribute('inert'),
      ariaHidden: element.getAttribute('aria-hidden'),
    }));
    for (const element of covered) {
      element.setAttribute('inert', '');
      element.setAttribute('aria-hidden', 'true');
    }
    // The scope clips the sheet (its slide-in, the scrim's corners) only while it is open, so
    // the card's own menus and popovers are never clipped the rest of the time.
    const scopeStyle = { overflow: scope.style.overflow, position: scope.style.position };
    scope.style.overflow = 'hidden';
    if (getComputedStyle(scope).position === 'static') scope.style.position = 'relative';
    // Marks the scope while a sheet is open, so a control pinned above the sheet's layer (the
    // overlay's fixed Give bar) can step aside: `[[data-pane-sheet-open]_&]:…`.
    scope.setAttribute(PANE_SHEET_OPEN_ATTR, '');
    return () => {
      scope.removeAttribute(PANE_SHEET_OPEN_ATTR);
      scope.style.overflow = scopeStyle.overflow;
      scope.style.position = scopeStyle.position;
      for (const { element, inert, ariaHidden } of saved) {
        if (inert === null) element.removeAttribute('inert');
        else element.setAttribute('inert', inert);
        if (ariaHidden === null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', ariaHidden);
      }
    };
  }, []);

  React.useEffect(() => {
    closeRef.current?.focus({ preventScroll: true });
  }, []);

  const sheet = (
    <>
      <div
        aria-hidden="true"
        data-faq-drawer-scrim=""
        className="absolute inset-0 z-20 bg-[color:var(--cs-drawer-backdrop)] animate-in fade-in-0 duration-[var(--cs-motion-fast)]"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        // Marks a pane sheet for popovers inside it (the schedule pickers): the card clips the
        // sheet, so they render to the document body instead of into the dialog.
        data-pane-sheet=""
        ref={dialogRef}
        className={`absolute inset-x-0 bottom-0 z-30 flex ${height === 'content' ? 'max-h-[var(--cs-pane-sheet-height)]' : 'h-[var(--cs-pane-sheet-height)]'} flex-col overflow-hidden rounded-t-[var(--cs-card-radius)] border-t border-[color:var(--cs-border-control)] bg-[color:var(--cs-surface-0)] shadow-[shadow:var(--cs-sheet-shadow)] animate-in slide-in-from-bottom duration-[var(--cs-drawer-duration)]`}
      >
        <div className="flex h-[var(--cs-size-title-strip)] shrink-0 items-center justify-between gap-[var(--cs-space-3)] border-b border-[color:var(--cs-border-control-subtle)] bg-[color:var(--cs-surface-1)] px-5">
          <h2
            id={titleId}
            className="m-0 text-[length:var(--cs-title-strip-font-size)] leading-[var(--cs-title-strip-line-height)] font-semibold text-[color:var(--cs-text-primary)]"
          >
            {title}
          </h2>
          <button
            ref={closeRef}
            type="button"
            aria-label={closeLabel}
            onClick={onClose}
            className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), '[&_svg]:size-[var(--cs-icon-lg)]')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" className="[stroke-width:var(--cs-icon-stroke)]" aria-hidden="true">
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>
        <div className="cs-scrollbar-hidden min-h-0 flex-1 overflow-y-auto px-5 pb-5">{children}</div>
        {/* A pinned action row (e.g. Save) below the scroll body. */}
        {footer && (
          <div className="shrink-0 border-t border-[color:var(--cs-border-control-subtle)] px-5 py-[var(--cs-space-4)]">
            {footer}
          </div>
        )}
      </div>
    </>
  );

  return container ? createPortal(sheet, container) : sheet;
}
